import { Injectable, inject } from '@angular/core';
import moment from 'moment-timezone';

import { DEFAULT_LANGUAGE } from '@models/system';
import { DATE_TIME_FORMATS } from '@shared/consts';
import { UserStore } from '@signal-store/user.store';
import { CipoDateTimePickerType } from '../common';

@Injectable({
  providedIn: 'root',
})
export class DateTimeFormatService {
  private userStore = inject(UserStore);

  private defaultDateTimeFormats = {
    parseInput: 'MM/DD/YYYY',
    fullPickerInput: 'MM/DD/YYYY hh:mm A',
    datePickerInput: 'MM/DD/YYYY',
    timePickerInput: 'hh:mm A',
    monthYearLabel: 'MMM YYYY',
  };

  getFormats() {
    const dateFormat = this.userStore.calculatedUserSettings().dateFormat;
    const timeFormat = this.userStore.calculatedUserSettings().timeFormat;
    if (!dateFormat || !timeFormat) {
      return this.defaultDateTimeFormats;
    }
    return {
      ...this.defaultDateTimeFormats,
      parseInput: dateFormat,
      fullPickerInput: `${dateFormat} ${timeFormat}`,
      datePickerInput: dateFormat,
      timePickerInput: timeFormat,
    };
  }

  getLocale() {
    return this.userStore.language() ?? DEFAULT_LANGUAGE;
  }

  transformIntoOwlLocalDateValue(
    utcDate: string,
    pickerType: CipoDateTimePickerType,
    ianaTimeZoneId: string,
  ): moment.Moment {
    if (!utcDate) {
      return null;
    }

    /* Sometimes the date form BE does not have the Z at the end */
    if (!utcDate.endsWith('Z')) {
      utcDate = utcDate + 'Z';
    }

    // date formatting is special, because the time part is ignored, so we need to use the current time in the local timezone to be handled correctly by owl picker
    return pickerType === 'date'
      ? moment.tz(utcDate.split('T')[0], ianaTimeZoneId)
      : moment.tz(utcDate, ianaTimeZoneId).clone().local(true);
  }

  getValueAsIso(date: moment.Moment, pickerType: CipoDateTimePickerType, ianaTimeZoneId: string): string {
    if (!date) {
      return null;
    }

    // Offset for the user's timezone
    const userUtcOffset = moment.tz(new Date(date.toISOString()), ianaTimeZoneId).utcOffset();
    // Offset for the browser timezone
    const browserUtcOffset = -new Date(date.toISOString()).getTimezoneOffset();

    // owl-date-time-picker returns a Moment object in local browser time zone
    // but the offset is not always the same as the browser timezone offset
    // it can be the user timezone offset if the user timezone is the same as the browser timezone
    // or it can be a different offset if the user timezone is different than the browser timezone
    // so we need to handle these cases to get the correct UTC ISO string

    if (pickerType === 'date') {
      // adds the user timezone offset to the date to get the correct UTC date as we want the time to be midnight
      return date.format(DATE_TIME_FORMATS.dateIso);
    }

    if (date instanceof moment && !date.isUTC() && date.utcOffset() === browserUtcOffset) {
      return moment(date.toDate())
        .add(-userUtcOffset + browserUtcOffset, 'minutes')
        .utc()
        .toISOString();
    }

    if (date instanceof moment && !date.isUTC() && date.utcOffset() === userUtcOffset) {
      return date.toISOString();
    }
    return null;
  }

  isEqual(date1: string | string[], date2: string | string[]): boolean {
    if (Array.isArray(date1) !== Array.isArray(date2)) {
      return false;
    }
    if (Array.isArray(date1) && Array.isArray(date2)) {
      return date1.length === date2.length && date1.every((d, i) => moment(d).isSame(moment(date2[i])));
    }
    return moment(date1).isSame(moment(date2));
  }
}
